import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { editBonusModalSchema } from './form/validations'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { Textarea } from '@components/shared/Textarea'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { useUser } from '@hooks/useUser'
import { useBonus } from '../../context'

import type { EditBonusModalProps } from './types'
import type { EditBonusModalFormProps } from './form/types'

import * as S from './styles'

export const EditBonusModal = ({ data, open, onClose, canEdit = true, ...rest }: EditBonusModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<EditBonusModalFormProps>({
    defaultValues: initialFormValues,
    resolver: editBonusModalSchema as unknown as Resolver<EditBonusModalFormProps>
  })

  const { filiData } = useUser()
  const { bonusData, isEditBonusLoading, editBonus } = useBonus()

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)

  const onError = (errors: FieldErrors<EditBonusModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: EditBonusModalFormProps) => {
    if (!canEdit) return

    editBonus({ ...formData, ...data, ...filiData }).then((success) => success && onClose(false))
  }

  const handleCalcBonusTotal = (adjustmentValue: number) => {
    const consumptionTotal = data?.previewConsumptionAmount || 0
    const bonusTotal = (consumptionTotal * adjustmentValue) / 100
    const deviation = adjustmentValue - (bonusData?.budgetPercentage! || 0)

    methods.setValue('bonificationTotal', bonusTotal.toFixed(2))
    methods.setValue('deviation', deviation.toFixed(2))
  }

  useEffect(() => {
    !!data && methods.reset(setInitialValues(data!))
  }, [data, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        width={870}
        open={open}
        heightContainer={isMobile ? '100%' : 'auto'}
        title="Editar Bonificação"
        headerIcon={<EditIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Salvar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isEditBonusLoading
        }}
        {...rest}
      >
        <S.Title>Preencha todos os campos antes de confirmar o ajuste da bonificação.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={5}>
              <Input name="consumptionTotal" label="Total Consumo (R$)" maskType="currency" disabled />
              <Input name="bonificationTotal" label="Total Bonificação (R$)" maskType="currency" disabled />
              <Input name="bonificationPercentage" label="Média Bonif. (%)" placeholder="0,00" disabled />
              <Input name="deviation" label="Desvio (%)" placeholder="0,00" disabled />
              <Input
                required
                name="adjustment"
                label="Ajuste (%)"
                placeholder="0,00"
                maskType="percentage"
                disabled={!canEdit || isEditBonusLoading}
                onBlur={({ target }) => {
                  methods.setValue('adjustment', Number(target.value.replace(',', '.')).toFixed(2))
                  handleCalcBonusTotal(Number(target.value.replace(',', '.')))
                }}
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit || isEditBonusLoading}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
